import { Injectable, inject } from '@angular/core';
import { PreloadingStrategy, Route, Router, NavigationEnd } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class CustomPreloadingStrategy implements PreloadingStrategy {
    private readonly router = inject(Router);
    private readonly loadedRoutes = new Set<string>();

    constructor() {
        // Subscribe to navigation events to handle preloadRoutes
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.handlePreloadRoutes(event.urlAfterRedirects);
        });
    }

    preload(route: Route, load: () => Observable<any>): Observable<any> {
        // Preload routes that have data.preload set to true
        if (route.data?.['preload']) {
            return load();
        }
        return of(null);
    }

    private handlePreloadRoutes(currentUrl: string): void {
        // Find the current route configuration
        const route = this.findRouteByUrl(currentUrl);
        if (route?.data?.['preloadRoutes']) {
            const routesToPreload = route.data['preloadRoutes'] as string[];
            routesToPreload.forEach(routePath => {
                if (!this.loadedRoutes.has(routePath)) {
                    this.preloadRoute(routePath);
                    this.loadedRoutes.add(routePath);
                }
            });
        }
    }

    private findRouteByUrl(url: string): Route | undefined {
        // Remove query params and fragments
        const cleanUrl = url.split('?')[0].split('#')[0];
        const pathSegments = cleanUrl.split('/').filter(s => s);
        const targetPath = pathSegments[0] || '';

        return this.router.config.find(route => route.path === targetPath);
    }

    private preloadRoute(routePath: string): void {
        const route = this.router.config.find(r => r.path === routePath);
        if (route?.loadComponent) {
            // Trigger the lazy load
            const loader = route.loadComponent();
            if (loader && typeof loader === 'object' && 'then' in loader) {
                (loader as Promise<any>).catch((err: any) => {
                    console.error('Failed to preload route:', routePath, err);
                });
            }
        } else if (route?.loadChildren) {
            const loader = route.loadChildren();
            if (loader && typeof loader === 'object' && 'then' in loader) {
                (loader as Promise<any>).catch((err: any) => {
                    console.error('Failed to preload route:', routePath, err);
                });
            }
        }
    }
}
